import React from "react";
import { Button, HistoryIcon } from "@pd/pd-ui-kit";
import { Flex } from "antd";
import { ColumnsType } from "antd/es/table";
import { TFunction } from "i18next";

import { ActIProps } from "~/entity/Ivideon/model/schema";
import {
  TYPE_OPEN_MODAL,
  TypeOpenModal,
} from "~/pages/RegisterOfDocuments/options";
import { dateFormat } from "~/shared/lib/DateFormat/dateFormat";

import s from "~/pages/RegisterOfDocuments/RegisterOfDocuments.less";

export interface RegisterOfDocumentsRow {
  id: number;
  dealer: string;
  history: string;
  act: string;
  sum: string;
  date: string;
  buttons: string;
  acts: ActIProps[] | [];
}

type ActItem = ActIProps & {
  actFile?: { id?: number; link: string; name: string };
};

const getActFile = (item: ActItem) => item.actFile ?? item.act;

const colTitle = (t: TFunction, key: string) => {
  const k = `registerOfDocuments.${key}`;
  const tr = t(k);
  return tr !== k ? tr : key;
};

const renderActsBlock = (
  optionName: "act" | "sum" | "date" | "buttons" | "status",
  dataObject: RegisterOfDocumentsRow,
  openModalState: (
    prop: TypeOpenModal,
    actId: number,
    dealerActId: number,
  ) => void,
  t: TFunction,
  deacceptGroups?: boolean,
  createDealerLicenses?: boolean,
  getLicenses?: boolean,
) => {
  const acts = dataObject?.acts;
  if (!Array.isArray(acts)) {
    return null;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {acts.map((item) => {
        const actFile = getActFile(item as ActItem);
        const hasFile = Boolean(actFile?.id);
        const canManage = item.actStatus === "created";
        const canViewCodes = item.actAccepted && !canManage;
        return (
          <ul key={item.actId}>
            <li className={s.acts}>
              {optionName === "act" && (
                <a href={actFile.link} target="_blank" rel="noreferrer">
                  {actFile.name}
                </a>
              )}
              {optionName === "sum" && item.actSum}
              {optionName === "date" &&
                dateFormat(new Date(item.actStartDate), { type: "date" })}
              {optionName === "status" && item.actStatus && (
                <span>{t(`registerOfDocuments.status.${item.actStatus}`)}</span>
              )}
              {optionName === "buttons" && (
                <Flex
                  vertical
                  gap={5}
                  align="flex-start"
                  className={s.buttonsCell}
                >
                  {hasFile && canViewCodes && getLicenses && (
                    <Button
                      bold
                      color="secondary"
                      variant="filled"
                      fontSize="10"
                      onClick={() =>
                        openModalState(
                          TYPE_OPEN_MODAL.code,
                          item.actId,
                          dataObject.id,
                        )
                      }
                      radius="30"
                      size="small"
                    >
                      Посмотреть коды
                    </Button>
                  )}
                  {hasFile && canManage && (
                    <Flex gap={5} align="flex-start">
                      {deacceptGroups && (
                        <Button
                          bold
                          color="secondary"
                          fontSize="10"
                          onClick={() =>
                            openModalState(
                              TYPE_OPEN_MODAL.deacceptAct,
                              item.actId,
                              dataObject.id,
                            )
                          }
                          radius="5"
                          size="small"
                        >
                          Отклонить
                        </Button>
                      )}
                      {createDealerLicenses && (
                        <Button
                          bold
                          color="primary"
                          fontSize="10"
                          onClick={() =>
                            openModalState(
                              TYPE_OPEN_MODAL.acceptAct,
                              item.actId,
                              dataObject.id,
                            )
                          }
                          radius="5"
                          size="small"
                        >
                          Принять
                        </Button>
                      )}
                    </Flex>
                  )}
                </Flex>
              )}
            </li>

            {item.invoices?.map((invoice) => (
              <li key={invoice.id} className={s.invoice}>
                {optionName === "act" && (
                  <a href={invoice.file.link} target="_blank" rel="noreferrer">
                    {invoice.file.name}
                  </a>
                )}
                {optionName === "sum" && invoice.sum}
                {optionName === "date" &&
                  dateFormat(new Date(invoice.startDate), { type: "date" })}
                {optionName === "buttons" && null}
              </li>
            ))}
          </ul>
        );
      })}
    </div>
  );
};

export const getRegisterOfDocumentsColumns = (
  t: TFunction,
  openModalState: (
    prop: TypeOpenModal,
    actId: number,
    dealerActId: number,
  ) => void,
  onHistoryNavigate: (dealerId: number, dealerName: string) => void,
  deacceptGroups: boolean,
  createDealerLicenses: boolean,
  getLicenses: boolean,
): ColumnsType<RegisterOfDocumentsRow> => [
  {
    title: colTitle(t, "history"),
    key: "history",
    align: "center",
    width: 26,
    render: (_, dataObject) => (
      <div
        className={s.history}
        onClick={() => onHistoryNavigate(dataObject.id, dataObject.dealer)}
      >
        <HistoryIcon size={16} fill="#495A64" />
      </div>
    ),
  },
  {
    title: colTitle(t, "id"),
    dataIndex: "id",
    key: "id",
  },
  {
    title: colTitle(t, "dealer"),
    dataIndex: "dealer",
    key: "dealer",
  },
  {
    title: colTitle(t, "act"),
    key: "act",
    render: (_, record) => renderActsBlock("act", record, openModalState, t),
  },
  {
    title: colTitle(t, "sum"),
    key: "sum",
    render: (_, record) => renderActsBlock("sum", record, openModalState, t),
  },
  {
    title: colTitle(t, "date"),
    key: "date",
    render: (_, record) => renderActsBlock("date", record, openModalState, t),
  },
  {
    title: t("registerOfDocuments.statusColumn", "Статус"),
    key: "status",
    render: (_, record) => renderActsBlock("status", record, openModalState, t),
  },
  {
    title: colTitle(t, "buttons"),
    key: "buttons",
    render: (_, record) =>
      renderActsBlock(
        "buttons",
        record,
        openModalState,
        t,
        deacceptGroups,
        createDealerLicenses,
        getLicenses,
      ),
  },
];
